<template>
  <div class="content-tab">
    <!-- Drafting progress — on completion the checklist stays visible in its
         finished state (all sections ✓, 100%) above the generated article,
         rather than vanishing the moment writing ends. -->
    <div v-if="showProgress" class="draft-progress" :class="{ 'is-done': !drafting }">
      <div class="draft-progress-head">
        <h4>{{ drafting ? 'Writing in progress' : 'Your article is ready' }}</h4>
        <span class="word-pill counter">{{ drafting ? sectionCounter : 'Done' }}</span>
      </div>
      <div class="progress-bar-wrap"><div class="progress-bar" :style="{ width: barPct + '%' }"></div></div>
      <div class="progress-list">
        <div v-for="(s, i) in sections" :key="i" class="progress-row" :class="rowStatus(s, i)">
          <div class="check">
            <span v-if="rowStatus(s, i) === ''">✓</span>
            <div v-else-if="rowStatus(s, i) === 'active'" class="spinner"></div>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="label">{{ s.label }}</div>
          <div v-if="s.target_words" class="word-pill">{{ s.word_count || '-' }} / {{ s.target_words }}</div>
        </div>
      </div>
      <div class="draft-progress-foot">
        <div v-if="drafting" class="current-preview">Currently: <strong>{{ activeLabel }}</strong></div>
        <div v-else class="current-preview">All sections written. <strong>Review below ↓</strong></div>
      </div>
    </div>

    <!-- Generated blocks -->
    <div v-if="!drafting" ref="blocksWrap" class="draft-blocks">
      <!-- Section navigator (sticky): jump between the article's text sections
           with prev/next, a live "N / total" counter and a "Jump to" menu.
           Visual blocks (images/tables/etc.) are managed from Structure, so only
           the writable text sections are navigable here. -->
      <div v-if="navBlocks.length > 1" class="section-nav">
        <button class="sec-nav-btn" :disabled="currentSectionIdx === 0" title="Previous section" @click="scrollToSection(currentSectionIdx - 1)">‹</button>
        <button class="sec-nav-btn" :disabled="currentSectionIdx === navBlocks.length - 1" title="Next section" @click="scrollToSection(currentSectionIdx + 1)">›</button>
        <div class="sec-nav-info">
          <span class="counter">{{ currentSectionIdx + 1 }} / {{ navBlocks.length }}</span>
          <span class="current">{{ currentTitle }}</span>
        </div>
        <div ref="secJump" class="sec-nav-jump">
          <button class="sec-nav-jump-btn" @click.stop="jumpOpen = !jumpOpen">Jump to ▾</button>
          <div class="sec-nav-dropdown" :class="{ open: jumpOpen }">
            <div
              v-for="(s, i) in navBlocks"
              :key="s.id"
              class="sec-nav-item"
              :class="{ current: i === currentSectionIdx }"
              @click="scrollToSection(i)"
            >
              <span class="idx">{{ i + 1 }}</span>
              <span class="title-txt">{{ s.title }}</span>
              <span v-if="s.locked" class="locked-ind">🔒</span>
              <span class="words">{{ s.words }}w</span>
            </div>
          </div>
        </div>
      </div>

      <block-item
        v-for="block in orderedBlocks"
        :key="block.id"
        :block="block"
        :image-busy="imageBusy"
        @edit="(p) => $emit('edit', p)"
        @lock="(b) => $emit('lock', b)"
        @delete="(b) => $emit('delete', b)"
        @regenerate="(p) => $emit('regenerate', p)"
        @again="(b) => $emit('again', b)"
        @toc-options="(p) => $emit('toc-options', p)"
        @history="(b) => $emit('history', b)"
        @image="(b) => $emit('image', b)"
        @alt-edit="(p) => $emit('alt-edit', p)"
      />
    </div>
  </div>
</template>

<script>
import BlockItem from "./BlockItem.vue";

// Block types that render as a writable text section (and are therefore
// reachable via the section navigator). Visual blocks are excluded — they are
// managed from the Structure tab and carry no editable body here.
const NAV_TYPES = ["intro", "h2", "h3", "faq"];

export default {
  name: "AiwV2ContentTab",
  components: { BlockItem },
  props: {
    blocks: { type: Array, default: () => [] },
    drafting: { type: Boolean, default: false },
    progress: { type: Object, default: () => ({}) },
    // True while any image is generating — disables other blocks' image buttons
    // so image tasks run one at a time (see DraftStep.onGenerateImage).
    imageBusy: { type: Boolean, default: false },
  },
  data() {
    return {
      currentSectionIdx: 0,
      jumpOpen: false,
    };
  },
  computed: {
    orderedBlocks() {
      return this.blocks.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    // Every question text carried by faq visual blocks (lowercased) — an h3
    // block with one of these titles is an FAQ question wherever it sits.
    faqQuestionTexts() {
      const set = new Set();
      this.blocks
        .filter((b) => b.type === "faq" && b.html)
        .forEach((b) => {
          try {
            const doc = new DOMParser().parseFromString(b.html, "text/html");
            doc.body.querySelectorAll("h3, h4, .pop-faq-q, .question").forEach((n) => {
              const t = (n.textContent || "").trim().toLowerCase();
              if (t) set.add(t);
            });
          } catch (e) { /* ignore malformed block html */ }
        });
      return set;
    },
    // Navigable text sections, in document order. FAQ question H3s are excluded
    // (mirrors build_toc's swallow rule): once the FAQ section starts — an
    // FAQ-titled H2 (any planner wording) or a faq block — following H3s are
    // skipped until the next non-FAQ H2; an h3 matching a faq-block question
    // text is skipped wherever it sits. The FAQ section entry itself stays.
    navBlocks() {
      const isFaqTitle = (t) => {
        const s = (t || "").trim().toLowerCase();
        return s.indexOf("frequently asked questions") === 0 || /^faqs?\b/.test(s);
      };
      const out = [];
      let inFaq = false;
      this.orderedBlocks.forEach((b) => {
        if (!NAV_TYPES.includes(b.type)) return;
        const title = this.sectionTitle(b);
        if (b.type === "h2") inFaq = isFaqTitle(title);
        else if (b.type === "faq") inFaq = true;
        else if (b.type === "h3" && (inFaq || this.faqQuestionTexts.has(title.trim().toLowerCase()))) return;
        out.push({ id: b.id, title, words: b.word_count || 0, locked: !!b.locked });
      });
      return out;
    },
    currentTitle() {
      const s = this.navBlocks[this.currentSectionIdx];
      return s ? s.title : "";
    },
    sections() {
      return this.progress.sections || [];
    },
    // Keep the panel up while writing, and once done as long as we have both the
    // per-section progress and the generated blocks to review beneath it.
    showProgress() {
      return this.drafting || (this.sections.length > 0 && this.blocks.length > 0);
    },
    barPct() {
      return this.drafting ? (this.progress.pct || 0) : 100;
    },
    // The draft task writes cluster-by-cluster and only flips finished sections
    // to 'done', so the first not-yet-done section is the one being written now.
    activeIndex() {
      if (!this.drafting) return -1;
      return this.sections.findIndex((s) => s.status !== "done");
    },
    // The foot's "Currently" label and the header counter both derive from the
    // active section (not the backend's separate `progress.label`, which lags a
    // step behind the section statuses) so the list, the counter, and the label
    // always name the same section.
    activeLabel() {
      if (!this.drafting) return "";
      const s = this.sections[this.activeIndex];
      return s ? s.label : (this.progress.label || "Preparing your brief");
    },
    sectionCounter() {
      if (this.activeIndex < 0 || !this.sections.length) return "Starting…";
      return `Section ${this.activeIndex + 1} of ${this.sections.length}`;
    },
  },
  watch: {
    // Blocks get replaced wholesale after generation / edits / regenerations —
    // rebuild the scroll-spy against the new elements and keep the index valid.
    blocks() {
      this.$nextTick(this.refreshNav);
    },
    drafting(v) {
      if (!v) this.$nextTick(this.refreshNav);
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocClick);
    if (!this.drafting) this.$nextTick(this.refreshNav);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
    this.teardownObserver();
    if (this._flash) window.clearTimeout(this._flash);
  },
  methods: {
    rowStatus(s, i) {
      // Finished view: every section reads as done (✓), regardless of the last
      // progress snapshot.
      if (!this.drafting) return "";
      if (s.status === "done") return "";
      if (s.status === "writing" || s.status === "active") return "active";
      if (i === this.activeIndex) return "active";
      return "pending";
    },
    // Section label for the navigator / jump menu. The intro has no heading of
    // its own, and an FAQ block may omit one, so fall back to friendly labels.
    sectionTitle(b) {
      if (b.type === "intro") return "Introduction";
      // Fixed label BEFORE the heading parse: the faq block's html titles each
      // QUESTION with an h4, so the first question would otherwise become the
      // block's nav label (matches the FAQ card's own header instead).
      if (b.type === "faq") return "Frequently asked questions";
      const html = b.html || "";
      if (html) {
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const h = doc.body.querySelector("h1, h2, h3, h4");
          const t = h && (h.textContent || "").trim();
          if (t) return t;
        } catch (e) { /* fall through to defaults */ }
      }
      return b.type === "h3" ? "Subsection" : "Section";
    },
    // Resolve each nav section to its live element BY BLOCK ID — never by
    // position: non-nav cards with the same class (e.g. the TOC card) and
    // filtered-out FAQ h3s would skew any index-based lineup.
    navEls() {
      const wrap = this.$refs.blocksWrap;
      if (!wrap) return [];
      return this.navBlocks.map((s) => wrap.querySelector('[data-block-id="' + s.id + '"]'));
    },
    scrollToSection(idx) {
      const els = this.navEls();
      if (idx < 0 || idx >= els.length) return;
      this.currentSectionIdx = idx;
      this.jumpOpen = false;
      const el = els[idx];
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Briefly flash the target so the jump is obvious.
      el.style.transition = "box-shadow .25s";
      el.style.boxShadow = "0 0 0 3px var(--pop-orange-soft, #ffeddd)";
      if (this._flash) window.clearTimeout(this._flash);
      this._flash = window.setTimeout(() => { el.style.boxShadow = ""; }, 700);
    },
    refreshNav() {
      const n = this.navBlocks.length;
      if (this.currentSectionIdx > n - 1) this.currentSectionIdx = Math.max(0, n - 1);
      this.setupObserver();
    },
    // Scroll-spy: keep the counter/current title in sync with what the reader is
    // looking at. IntersectionObserver against the viewport works regardless of
    // which ancestor actually scrolls, so no scroll-container hunting is needed.
    setupObserver() {
      this.teardownObserver();
      const els = this.navEls();
      if (!els.length || typeof IntersectionObserver === "undefined") return;
      const visible = new Array(els.length).fill(false);
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const i = els.indexOf(e.target);
          if (i >= 0) visible[i] = e.isIntersecting;
        });
        const i = visible.findIndex(Boolean);
        if (i >= 0 && i !== this.currentSectionIdx) this.currentSectionIdx = i;
      }, { root: null, rootMargin: "-120px 0px -60% 0px", threshold: 0 });
      els.forEach((el) => { if (el) this._io.observe(el); });
    },
    teardownObserver() {
      if (this._io) { this._io.disconnect(); this._io = null; }
    },
    onDocClick(e) {
      if (!this.jumpOpen) return;
      const jump = this.$refs.secJump;
      if (jump && !jump.contains(e.target)) this.jumpOpen = false;
    },
  },
};
</script>

<style scoped>
.content-tab { max-width: 820px; margin: 0 auto; }

/* ----- Drafting progress ----- */
.draft-progress { margin-bottom: 12px; }
.draft-progress-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.draft-progress-head h4 { font-size: 16px; font-weight: 700; margin: 0; color: var(--text, #052e33); }
.progress-bar-wrap { height: 6px; background: var(--surface-2, #f7f7f7); border-radius: 999px; overflow: hidden; margin-bottom: 14px; border: 1px solid var(--border, #e4e4e4); }
.progress-bar { height: 100%; background: linear-gradient(90deg, var(--pop-orange, #ff5700) 0%, #ff9966 100%); width: 0%; transition: width 1.1s ease-out; border-radius: 999px; }
.progress-list { display: flex; flex-direction: column; gap: 2px; }
/* Icon aligned to the first line of the label (labels can wrap), not the vertical
   center of the whole wrapped block. */
.progress-row { display: flex; align-items: flex-start; gap: 12px; padding: 8px 0; font-size: 13.5px; }
.progress-row .check { width: 20px; height: 20px; border-radius: 50%; background: var(--pop-green, #25a97a); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.progress-row.active .check { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange, #ff5700); border: 1.5px solid var(--pop-orange, #ff5700); animation: pulse 1.6s ease-in-out infinite; }
.progress-row.pending .check { background: var(--surface-2, #f7f7f7); color: var(--text-muted, #9aa6a8); border: 1.5px solid var(--border-strong, #ccc); }
/* In-progress row: a rotating spinner inside the check (design handoff). */
.progress-row .check .spinner { width: 10px; height: 10px; border-radius: 50%; border: 1.5px solid transparent; border-top-color: var(--pop-orange, #ff5700); border-right-color: var(--pop-orange, #ff5700); animation: spin 0.7s linear infinite; }
@keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,87,0,.4); } 50% { box-shadow: 0 0 0 6px rgba(255,87,0,0); } }
@keyframes spin { to { transform: rotate(360deg); } }
.progress-row .label { flex: 1; font-weight: 500; color: var(--text-2, #014751); line-height: 20px; }
.progress-row.active .label { font-weight: 600; color: var(--text, #052e33); }
.progress-row.pending .label { color: var(--text-muted, #9aa6a8); }
.progress-row .word-pill { background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); font-size: 11px; padding: 2px 7px; border-radius: 4px; font-weight: 500; }
.progress-row.active .word-pill { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange, #ff5700); }
/* Header counter ("Section N of M" / "Done"): plain right-aligned text, matching
   the design — its .word-pill rules are scoped to .progress-row, so the design's
   counter renders with no chip background. */
.word-pill.counter { background: none; color: inherit; font-size: inherit; padding: 0; border-radius: 0; font-weight: inherit; margin-left: auto; }
.draft-progress-foot { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border, #e4e4e4); display: flex; gap: 10px; align-items: center; }
.current-preview { flex: 1; font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.current-preview strong { color: var(--text, #052e33); font-weight: 600; }

/* ----- Section navigator (sticky "Jump to" banner) ----- */
.section-nav { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 10px 14px; margin-bottom: 14px; display: flex; align-items: center; gap: 10px; position: sticky; top: 0; z-index: 10; box-shadow: var(--shadow-sm, 0 1px 2px rgba(15,20,25,.03)); }
.sec-nav-btn { background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); color: var(--text-2, #014751); width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; font-family: inherit; transition: all .15s; }
.sec-nav-btn:hover:not(:disabled) { background: #fff; color: var(--text, #052e33); border-color: var(--border-strong, #ccc); }
.sec-nav-btn:disabled { opacity: .4; cursor: not-allowed; }
.sec-nav-info { flex: 1; display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--text-3, #6b7b7e); min-width: 0; }
.sec-nav-info .counter { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 11px; flex-shrink: 0; }
.sec-nav-info .current { color: var(--text, #052e33); font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sec-nav-jump { position: relative; }
.sec-nav-jump-btn { background: #fff; border: 1px solid var(--border-strong, #ccc); color: var(--text-2, #014751); padding: 6px 12px; border-radius: 7px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; }
.sec-nav-jump-btn:hover { background: var(--surface-2, #f7f7f7); }
.sec-nav-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); min-width: 320px; max-height: 60vh; overflow-y: auto; padding: 6px; z-index: 20; display: none; }
.sec-nav-dropdown.open { display: block; }
.sec-nav-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 6px; cursor: pointer; font-size: 13px; color: var(--text, #052e33); transition: background .15s; }
.sec-nav-item:hover { background: var(--surface-2, #f7f7f7); }
.sec-nav-item.current { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); font-weight: 600; }
.sec-nav-item .idx { width: 20px; height: 20px; border-radius: 4px; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.sec-nav-item.current .idx { background: var(--pop-orange, #ff5700); color: #fff; }
.sec-nav-item .title-txt { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sec-nav-item .locked-ind { color: var(--pop-amber, #f59e0b); font-size: 11px; }
.sec-nav-item .words { font-size: 10.5px; color: var(--text-3, #6b7b7e); flex-shrink: 0; }
.sec-nav-item.current .words { color: var(--pop-orange-deep, #b23d00); }

/* Leave room for the sticky nav when jumping to a section. */
.draft-blocks :deep(.draft-block) { scroll-margin-top: 64px; }
</style>
